import { vacationAllowanceRepository } from '../db/database';
import type { IsoDate, ProgressState, VacationAllowance } from '../models/types';
import {
  enableNotifications,
  hasNotificationPermission,
  isNotificationBlocked,
} from '../services/notifications';
import { getOrCreateProgressState } from '../services/dailyRollover';
import { getDayCover, type DayCover } from '../services/dayCover';
import { queueNotificationSync } from '../services/notificationSync';
import {
  getRemainingDays,
  isVacationDate,
  useVacationDay,
} from '../services/vacation';
import { wipeAllData } from '../services/resetData';
import { todayIso } from '../utils/date';
import { setDelegatedClick } from '../ui/delegatedClick';
import { renderIcon } from '../ui/icons';
import type { RouteId } from '../ui/tabBar';

/** Tatil hakkı kullanım onayı açık mı? */
let askingConfirmation = false;

/**
 * "Her şeyi sil" onayı hangi aşamada?
 *
 * İKİ aşamalı, çünkü bu işlem geri alınamaz ve yedeği yoktur: veriler
 * yalnızca bu telefonda durur. Tek dokunuşluk bir onay, cebinde yanlışlıkla
 * basılan bir düğmeyle yılların kaydını silebilirdi.
 */
let wipeStage: 'idle' | 'asking' | 'confirming' = 'idle';

/** Bildirim kurulumundan sonra gösterilecek kısa durum mesajı. */
let notificationMessage: string | null = null;

/**
 * Profil ana sayfası — yönetim ekranlarına açılan kapı.
 *
 * Not: "Temizlik Görevlerini Düzenle" bilinçli olarak BURADA DEĞİL.
 * Kullanıcı kararıyla o giriş, Temizlik ekranının içindeki sabit
 * düzenleme butonuna taşındı — görevleri düzenlemek isteyen kişi
 * zaten o ekranda oluyor.
 */

interface ProfileLink {
  label: string;
  route: RouteId;
}

/**
 * Liste kartları. Her dizi ayrı bir kart olur; ilişkili girişler
 * aynı kartta gruplanır.
 */
const LINK_GROUPS: ProfileLink[][] = [
  [{ label: 'Alışkanlıkları Düzenle', route: 'habitEdit' }],
  [
    { label: 'Detaylı Geçmiş', route: 'history' },
    { label: 'Ödüller', route: 'rewards' },
  ],
];

/** Sağa bakan ok — satırın tıklanabilir olduğunu anlatır. */
function renderChevron(): string {
  return `
    <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
      <path d="M1 1l6 6-6 6" stroke="oklch(70% 0.01 60)" stroke-width="2" fill="none"
            stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
}

function renderLinkGroup(group: ProfileLink[]): string {
  const rows = group
    .map(
      (link) => `
        <button type="button" class="list-row" data-route="${link.route}">
          <span class="list-row__label">${link.label}</span>
          ${renderChevron()}
        </button>
      `,
    )
    .join('');

  return `<div class="list-card">${rows}</div>`;
}

/**
 * Tatil hakkı banner'ı — yalnızca kullanılabilir hak varken görünür.
 *
 * §7.7'ye göre hak, 90 günlük kesintisiz seri sonunda açılır. Hak
 * tükendiğinde banner tamamen gizlenir; "0 gün hakkın var" demek
 * kullanıcıya bir şey kazandırmaz.
 *
 * Üç hâli var: kullanılabilir (buton görünür), bugün zaten kullanılmış
 * (buton yerine durum yazısı), ve onay bekliyor.
 */
function renderVacationBanner(
  allowance: VacationAllowance | undefined,
  today: IsoDate,
  cover: DayCover,
): string {
  const remaining = getRemainingDays(allowance);
  if (remaining <= 0) return '';

  // Hakkın hangi kilometre taşından geldiğini bulup gösteriyoruz —
  // kullanıcı bunu neyle kazandığını görsün.
  const streakGrants = allowance?.grants.filter((grant) => grant.source === 'dayStreak') ?? [];
  const lastMilestone = streakGrants.at(-1)?.streakMilestone;
  const usedToday = isVacationDate(allowance, today);

  let action: string;
  if (usedToday) {
    action = '<span class="vacation-banner__state">Bugün tatil günü</span>';
  } else if (cover === 'reward') {
    // §7.6 — Gün ödülle satın alındıysa zaten dondurulmuş; üstüne bir
    // de tatil hakkı harcamak karşılıksız bir kayıp olurdu.
    action = '<span class="vacation-banner__state">Bugün ödülle alındı</span>';
  } else if (askingConfirmation) {
    action = '';
  } else {
    action = '<button type="button" class="vacation-banner__use" data-use-vacation>Kullan</button>';
  }

  return `
    <div class="vacation-banner">
      <span class="vacation-banner__icon">${renderIcon('gift', { size: 26 })}</span>
      <div class="vacation-banner__body">
        <p class="vacation-banner__title">${remaining} Günlük Tatil Hakkın Var</p>
        ${
          lastMilestone
            ? `<p class="vacation-banner__note">${lastMilestone} günlük seri sayesinde kazanıldı</p>`
            : ''
        }
      </div>
      ${action}
    </div>
    ${askingConfirmation && cover === 'none' ? renderVacationConfirm(remaining) : ''}
  `;
}

/**
 * Kullanım onayı.
 *
 * Hak sınırlı ve harcandıktan sonra geri alınamıyor; tek dokunuşla
 * harcanmasın diye araya onay koyuyoruz.
 */
function renderVacationConfirm(remaining: number): string {
  return `
    <div class="challenge-confirm challenge-confirm--info">
      <p class="challenge-confirm__title">Bugün tatil günü olsun mu?</p>
      <p class="challenge-confirm__text">
        Bugün için ceza işlemeyecek ve gün serine otomatik sayılacak.
        Kalan hakkın ${remaining - 1} güne düşecek. Bu işlem geri alınamaz.
      </p>
      <div class="challenge-confirm__actions">
        <button type="button" class="challenge-confirm__no" data-abort-vacation>Vazgeç</button>
        <button type="button" class="challenge-confirm__yes challenge-confirm__yes--info"
                data-confirm-vacation>Evet, kullan</button>
      </div>
    </div>
  `;
}

/**
 * Bildirim kartı — izni buradan açılır.
 *
 * İzin olmadan ne günlük hatırlatma ne de seri bildirimi çalışır, o
 * yüzden bu kart Profil'de görünür bir yerde duruyor. iOS'ta izin
 * penceresi ancak uygulama ANA EKRANA EKLENDİYSE açılır.
 *
 * Not (§7.8): bildirime özel ses verilemez — iOS sistem varsayılan
 * sesini çalar. Seri kutlamasının kendi sesi, uygulama açıkken çalar.
 */
function renderNotificationCard(): string {
  if (!('Notification' in window)) {
    return `
      <div class="list-card notice-card">
        <p class="notice-card__title">Bildirimler kullanılamıyor</p>
        <p class="notice-card__text">
          Bildirimler yalnızca uygulama ana ekrana eklendiğinde çalışır.
          Safari'de Paylaş → "Ana Ekrana Ekle" yap.
        </p>
      </div>
    `;
  }

  if (isNotificationBlocked()) {
    return `
      <div class="list-card notice-card">
        <p class="notice-card__title">Bildirimler kapalı</p>
        <p class="notice-card__text">
          İzin daha önce reddedilmiş. Yeniden açmak için iOS
          Ayarlar → Bildirimler → Discipline yolunu kullan.
        </p>
      </div>
    `;
  }

  if (hasNotificationPermission()) {
    return `
      <div class="list-card notice-card">
        <p class="notice-card__title">Bildirimler açık</p>
        ${notificationMessage ? `<p class="notice-card__text">${notificationMessage}</p>` : ''}
      </div>
    `;
  }

  return `
    <div class="list-card notice-card">
      <p class="notice-card__title">Bildirimler kapalı</p>
      <p class="notice-card__text">
        Günlük hatırlatma ve seri bildirimleri için izin gerekiyor.
      </p>
      ${notificationMessage ? `<p class="notice-card__text">${notificationMessage}</p>` : ''}
      <button type="button" class="notice-card__action" data-enable-notifications>
        Bildirimleri Aç
      </button>
    </div>
  `;
}

/**
 * GELİŞTİRME ARACI kartı — yalnızca `npm run dev` sırasında görünür.
 *
 * Telefonda tarayıcı konsolu olmadığı için sahte veri üretmenin başka
 * yolu yok. `import.meta.env.DEV`, `npm run build` çıktısında `false`
 * sabitine dönüşür; bu blok ve bağlı olduğu `demoData` modülü üretim
 * paketine hiç girmez.
 */
function renderDevToolsCard(): string {
  if (!import.meta.env.DEV) return '';

  return `
    <div class="list-card notice-card notice-card--dev">
      <p class="notice-card__title">Geliştirme aracı</p>
      <p class="notice-card__text">
        Geçmiş, takvim ve nokta şeritleri ancak günler kapandıkça dolar.
        Bu ekranları dolu görmek için sahte bir geçmiş üretebilirsin.
      </p>
      <button type="button" class="notice-card__action" data-seed-demo>
        Sahte geçmiş oluştur
      </button>
    </div>
  `;
}

/**
 * TEHLİKELİ BÖLGE — geçmişi silip uygulamayı sıfırlar.
 *
 * Geliştirme aracının aksine bu kart ÜRETİMDE DE görünür (kullanıcı
 * isteği). Baştan başlamak isteyen birinin tek yolu bu; uygulamayı
 * silip yeniden kurmak ana ekran ikonunu ve bildirim iznini de
 * götürürdü.
 *
 * Silme iki adımda onaylanır ve ikinci adımın metni ne kaybedileceğini
 * açıkça sayar — `window.confirm` yerine uygulamanın kendi onay
 * kutusunu kullanıyoruz; ana ekrana eklenmiş bir PWA'da sistem
 * penceresi yabancı ve aceleye getiren bir şey gibi duruyor.
 */
function renderDangerZone(progress: ProgressState): string {
  if (wipeStage === 'idle') {
    return `
      <div class="list-card notice-card notice-card--danger-zone">
        <p class="notice-card__title">Geçmişi sil ve sıfırla</p>
        <p class="notice-card__text">
          Uygulamayı ilk günkü hâline döndürür. Alışkanlıklar, temizlik
          odaları, puan geçmişi ve seri sıfırlanır.
        </p>
        <button type="button" class="notice-card__action notice-card__action--danger"
                data-wipe-ask>
          Her şeyi sil ve sıfırla
        </button>
      </div>
    `;
  }

  // İkinci aşamada "Evet, sil" düğmesi yer değiştiriyor: ilk onaya
  // basan parmak, aynı noktaya ikinci kez basarak işlemi kazayla
  // tamamlayamasın.
  const isFinal = wipeStage === 'confirming';

  return `
    <!-- .challenge-confirm'in temel hâli zaten kırmızı (yıkıcı işlem
         rengi); ayrı bir varyant sınıfı gerekmiyor. -->
    <div class="challenge-confirm">
      <p class="challenge-confirm__title">
        ${isFinal ? 'Son kez soruyorum' : 'Her şey silinsin mi?'}
      </p>
      <p class="challenge-confirm__text">
        ${
          isFinal
            ? `${progress.currentStreak} günlük serin, ${progress.barValue} puanın ve
               tüm geçmişin kalıcı olarak silinecek. Bu işlemin geri dönüşü YOK —
               verilerin yalnızca bu telefonda duruyor, hiçbir yerde yedeği yok.`
            : `Alışkanlıkların, temizlik odaların, yapılacakların, challenge'ların
               ve tüm puan geçmişin silinecek. Uygulama ilk kurulum hâline dönecek.`
        }
      </p>
      <div class="challenge-confirm__actions">
        <button type="button" class="challenge-confirm__no" data-wipe-abort>Vazgeç</button>
        <button type="button" class="challenge-confirm__yes"
                ${isFinal ? 'data-wipe-confirm' : 'data-wipe-again'}>
          ${isFinal ? 'Evet, sil' : 'Devam et'}
        </button>
      </div>
    </div>
  `;
}

export async function renderProfilePage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  const today = todayIso();
  const [allowance, progress] = await Promise.all([
    vacationAllowanceRepository.get(),
    getOrCreateProgressState(),
  ]);
  // Bugün tatil ya da ödülle dondurulmuş mu? İkisi aynı gün
  // kullanılamaz (§7.6).
  const cover = getDayCover(today, allowance, progress);
  const redraw = (): Promise<void> => renderProfilePage(container, navigate);

  container.innerHTML = `
    <h1 class="page-title">Profil</h1>
    <p class="page-subtitle">Yönetim ve ayarlar</p>
    ${LINK_GROUPS.map(renderLinkGroup).join('')}
    ${renderVacationBanner(allowance, today, cover)}
    ${renderNotificationCard()}
    ${renderDevToolsCard()}
    ${renderDangerZone(progress)}
    <p class="profile-footnote">Bu alan zamanla genişleyecek.</p>
  `;

  // Sayfa tatil hakkı akışında kendini yeniden çiziyor.
  // `setDelegatedClick` önceki dinleyiciyi kaldırır — aksi halde her
  // çizimde bir dinleyici daha birikir ve tek dokunuş birden çok kez
  // işlenirdi.
  setDelegatedClick('profile', container, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // --- Geliştirme araçları (yalnızca dev) -------------------------
    // Modül DİNAMİK olarak yükleniyor: `import.meta.env.DEV` üretimde
    // `false` olduğu için bu dallar tamamen silinir ve `demoData`
    // pakete girmez.
    if (import.meta.env.DEV && target.closest('[data-seed-demo]')) {
      void (async () => {
        const { seedDemoHistory } = await import('../services/demoData');
        const summary = await seedDemoHistory();
        // Tam yeniden yükleme: tüm ekranlar veriyi baştan okusun.
        window.alert(
          `${summary.days} günlük sahte geçmiş oluşturuldu.\n` +
            `Bar: ${summary.barValue} · Seri: ${summary.currentStreak} ` +
            `(en uzun ${summary.longestStreak})\n` +
            `Tatil: ${summary.vacationDaysUsed} kullanıldı, ` +
            `${summary.vacationDaysLeft} kaldı · Ödül: ${summary.rewardDaysUsed} gün`,
        );
        window.location.reload();
      })();
      return;
    }

    // --- Tehlikeli bölge: her şeyi sil (üretimde de var) -------------
    if (target.closest('[data-wipe-ask]')) {
      wipeStage = 'asking';
      void redraw();
      return;
    }

    if (target.closest('[data-wipe-again]')) {
      wipeStage = 'confirming';
      void redraw();
      return;
    }

    if (target.closest('[data-wipe-abort]')) {
      wipeStage = 'idle';
      void redraw();
      return;
    }

    if (target.closest('[data-wipe-confirm]')) {
      void (async () => {
        // Onay bitti; ekranı kilitlemek yerine durumu sıfırlıyoruz —
        // silme başarısız olursa kullanıcı kartı yine baştan görür.
        wipeStage = 'idle';
        await wipeAllData();
        // Tam yeniden yükleme: `startApp` boş veritabanını baştan
        // tohumlasın ve tüm ekranlar sıfırdan kurulsun.
        window.location.reload();
      })();
      return;
    }

    // --- Bildirim izni ----------------------------------------------
    if (target.closest('[data-enable-notifications]')) {
      void (async () => {
        const result = await enableNotifications();
        if (result.ok) {
          // Program yalnızca izin varken gönderiliyor (§7.8.4 gizlilik
          // kararı). İzin şimdi alındığına göre, ilk eşitlemeyi hemen
          // tetikliyoruz — yoksa kullanıcı bir şey değiştirene kadar
          // sunucunun hatırlatacağı bir şeyi olmazdı.
          queueNotificationSync();
          notificationMessage = result.pushRegistered
            ? 'Hatırlatmalar bu cihaza gönderilecek.'
            : 'Uygulama içi bildirimler hazır. Zamanlı hatırlatmalar için sunucu ayarları tamamlanmalı.';
        } else if (result.reason === 'denied') {
          notificationMessage = 'İzin verilmedi.';
        } else if (result.reason === 'unsupported') {
          notificationMessage = 'Bu ortamda bildirim desteklenmiyor.';
        } else {
          notificationMessage = 'İzin alındı ama sunucuya abone olunamadı.';
        }
        await redraw();
      })();
      return;
    }

    // --- Tatil hakkı akışı ------------------------------------------
    if (target.closest('[data-use-vacation]')) {
      askingConfirmation = true;
      void redraw();
      return;
    }
    if (target.closest('[data-abort-vacation]')) {
      askingConfirmation = false;
      void redraw();
      return;
    }
    if (target.closest('[data-confirm-vacation]')) {
      void (async () => {
        await useVacationDay(today);
        askingConfirmation = false;
        await redraw();
      })();
      return;
    }

    // --- Liste satırları --------------------------------------------
    const row = target.closest<HTMLElement>('[data-route]');
    const route = row?.dataset['route'];
    if (route) void navigate(route as RouteId);
  });
}
